import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/App";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Users,
  Wallet,
  FileCheck,
  Loader2,
  RefreshCw,
  LogOut,
  BarChart3,
  Settings,
  Lock,
  Eye,
  EyeOff,
  CheckCircle
} from "lucide-react";
import {
  OverviewStats,
  VerificationsTab,
  AllPartnersTab,
  WithdrawalsTab,
  DocumentReviewDialog,
  RejectDocumentDialog
} from "@/components/admin";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats] = useState(null);
  
  // Partner verification state
  const [pendingVerifications, setPendingVerifications] = useState([]);
  const [allPartners, setAllPartners] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [partnerDocuments, setPartnerDocuments] = useState([]);
  const [showDocDialog, setShowDocDialog] = useState(false);
  const [processingDoc, setProcessingDoc] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState(null);
  
  // Withdrawals state
  const [withdrawals, setWithdrawals] = useState([]);
  const [processingWithdrawal, setProcessingWithdrawal] = useState(null);
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  // Check admin access
  useEffect(() => {
    if (user && user.user_type !== "admin") {
      toast.error("Admin access required");
      navigate("/");
    }
  }, [user, navigate]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get("/admin/dashboard");
      setStats(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to fetch admin stats:', error.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPendingVerifications = useCallback(async () => {
    try {
      const response = await api.get("/partner-verification/admin/pending");
      setPendingVerifications(response.data.pending_verifications || []);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to fetch pending verifications:', error.message);
      }
    }
  }, []);

  const fetchAllPartners = useCallback(async () => {
    try {
      const response = await api.get("/partner-verification/admin/all-partners");
      setAllPartners(response.data.partners || []);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to fetch partners:', error.message);
      }
    }
  }, []);

  const fetchWithdrawals = useCallback(async () => {
    try {
      const response = await api.get("/admin/withdrawals");
      setWithdrawals(response.data.withdrawals || []);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to fetch withdrawals:', error.message);
      }
    }
  }, []);

  useEffect(() => {
    if (user?.user_type === "admin") {
      fetchStats();
      fetchPendingVerifications();
      fetchAllPartners();
      fetchWithdrawals();
    }
  }, [user, fetchStats, fetchPendingVerifications, fetchAllPartners, fetchWithdrawals]);

  const viewPartnerDocuments = async (partnerId) => {
    try {
      const response = await api.get(`/partner-verification/admin/partner/${partnerId}`);
      setSelectedPartner(response.data.partner);
      setPartnerDocuments(response.data.documents || []);
      setShowDocDialog(true);
    } catch (error) {
      toast.error("Failed to fetch partner documents");
    }
  };

  const approveDocument = async (docId) => {
    setProcessingDoc(docId);
    try {
      await api.post(`/partner-verification/admin/approve/${docId}`);
      toast.success("Document approved");
      if (selectedPartner) {
        viewPartnerDocuments(selectedPartner.user_id);
      }
      fetchPendingVerifications();
      fetchAllPartners();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve document");
    } finally {
      setProcessingDoc(null);
    }
  };

  const openRejectDialog = (docId) => {
    setSelectedDocId(docId);
    setRejectReason("");
    setShowRejectDialog(true);
  };

  const rejectDocument = async () => {
    if (!rejectReason.trim()) {
      toast.error("Please provide a rejection reason");
      return;
    }
    
    setProcessingDoc(selectedDocId);
    try {
      await api.post(`/partner-verification/admin/reject/${selectedDocId}`, {
        reason: rejectReason
      });
      toast.success("Document rejected");
      setShowRejectDialog(false);
      if (selectedPartner) {
        viewPartnerDocuments(selectedPartner.user_id);
      }
      fetchPendingVerifications();
      fetchAllPartners();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject document");
    } finally {
      setProcessingDoc(null);
    }
  };

  const handleWithdrawalAction = async (withdrawalId, action) => {
    setProcessingWithdrawal(withdrawalId);
    try {
      await api.post(`/admin/withdrawals/${withdrawalId}/${action}`, {});
      toast.success(`Withdrawal ${action}ed`);
      fetchWithdrawals();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} withdrawal`);
    } finally {
      setProcessingWithdrawal(null);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    setChangingPassword(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword
      });
      toast.success("Password changed successfully!");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  const refreshAll = () => {
    fetchStats();
    fetchPendingVerifications();
    fetchAllPartners();
    fetchWithdrawals();
  };

  if (!user || user.user_type !== "admin") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Admin Navbar */}
      <nav className="sticky top-0 z-50 border-b border-border bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo-new.png" alt="Setuxpress" className="h-10" />
              
              <span className="px-2 py-0.5 bg-red-100 text-red-800 text-xs font-bold ml-2">ADMIN</span>
            </Link>
            
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground">{user.email}</span>
              <Button variant="outline" onClick={logout} className="rounded-none" data-testid="admin-logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-black tracking-tight">Admin Dashboard</h1>
            <p className="text-muted-foreground">Manage partners, withdrawals, and platform metrics</p>
          </div>
          <Button 
            variant="outline" 
            onClick={refreshAll}
            className="rounded-none gap-2"
            data-testid="refresh-admin-btn"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview" className="gap-2">
              <BarChart3 className="w-4 h-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="verifications" className="gap-2">
              <FileCheck className="w-4 h-4" />
              Verifications
              {pendingVerifications.length > 0 && (
                <span className="ml-1 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                  {pendingVerifications.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="partners" className="gap-2">
              <Users className="w-4 h-4" />
              All Partners
            </TabsTrigger>
            <TabsTrigger value="withdrawals" className="gap-2">
              <Wallet className="w-4 h-4" />
              Withdrawals
            </TabsTrigger>
            <TabsTrigger value="settings" className="gap-2">
              <Settings className="w-4 h-4" />
              Settings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <OverviewStats
              stats={stats}
              loading={loading}
              pendingVerificationsCount={pendingVerifications.length}
            />
          </TabsContent>

          <TabsContent value="verifications">
            <VerificationsTab
              pendingVerifications={pendingVerifications}
              onViewDocuments={viewPartnerDocuments}
            />
          </TabsContent>

          <TabsContent value="partners">
            <AllPartnersTab
              partners={allPartners}
              onViewDocuments={viewPartnerDocuments}
            />
          </TabsContent>

          <TabsContent value="withdrawals">
            <WithdrawalsTab
              withdrawals={withdrawals}
              processingWithdrawal={processingWithdrawal}
              onAction={handleWithdrawalAction}
            />
          </TabsContent>

          <TabsContent value="settings">
            <div className="max-w-md">
              <div className="border border-border p-6">
                <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <Lock className="w-5 h-5" />
                  Change Password
                </h2>
                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div>
                    <Label htmlFor="currentPassword">Current Password</Label>
                    <div className="relative mt-1">
                      <Input
                        id="currentPassword"
                        type={showCurrentPassword ? "text" : "password"}
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        placeholder="Enter current password"
                        className="rounded-none pr-10"
                        required
                        data-testid="current-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="newPassword">New Password</Label>
                    <div className="relative mt-1">
                      <Input
                        id="newPassword"
                        type={showNewPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Enter new password (min 8 characters)"
                        className="rounded-none pr-10"
                        required
                        minLength={8}
                        data-testid="new-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="confirmPassword">Confirm New Password</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      className="rounded-none mt-1"
                      required
                      data-testid="confirm-password-input"
                    />
                    {confirmPassword && newPassword !== confirmPassword && (
                      <p className="text-sm text-red-500 mt-1">Passwords do not match</p>
                    )}
                    {confirmPassword && newPassword === confirmPassword && newPassword.length >= 8 && (
                      <p className="text-sm text-green-500 mt-1 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" /> Passwords match
                      </p>
                    )}
                  </div>
                  
                  <Button 
                    type="submit" 
                    disabled={changingPassword || !currentPassword || !newPassword || newPassword !== confirmPassword}
                    className="w-full rounded-none"
                    data-testid="change-password-btn"
                  >
                    {changingPassword ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Changing Password...
                      </>
                    ) : (
                      <>
                        <Lock className="w-4 h-4 mr-2" />
                        Change Password
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <DocumentReviewDialog
        open={showDocDialog}
        onOpenChange={setShowDocDialog}
        selectedPartner={selectedPartner}
        documents={partnerDocuments}
        processingDoc={processingDoc}
        onApprove={approveDocument}
        onReject={openRejectDialog}
        onRefresh={() => selectedPartner && viewPartnerDocuments(selectedPartner.user_id)}
      />

      <RejectDocumentDialog
        open={showRejectDialog}
        onOpenChange={setShowRejectDialog}
        rejectReason={rejectReason}
        onReasonChange={setRejectReason}
        onConfirm={rejectDocument}
        processing={processingDoc}
      />
    </div>
  );
}
